import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { compact, startCase } from 'lodash'
import Button from 'app/components/Button'
import Combo from 'app/components/ui/combo/combo'
import FloatingSave from 'app/components/FloatingSave'
import FormComboAsyncV2 from 'app/components/form/FormComboAsyncV2'
import FormInput from 'app/components/form/FormInput'
import FormPanel from 'app/components/form/FormPanel'
import FormToggle from 'app/components/form/FormToggle'
import { SmartForm } from 'app/components/form/SmartForm'
import { LearnMoreLink } from 'app/components/shared/LearnMoreLink'
import UserLocationSelector from 'app/components/UserLocationSelector'
import { RoleSelector } from 'app/components/users/RoleSelector'
import WorkspaceSelector from 'app/components/WorkspaceSelector'
import { legacyRoleRank } from 'app/types/roles'
import { User, UserType } from 'app/types/entities'
import { WorkspaceJson } from 'app/types/entities'
import { hasRole } from 'app/utils'
import { debug } from 'lib/logging'
import type { UseUserDetailsReturn } from './useUserDetails'
import { createGqlFetcher } from 'app/lib/gqlFetcher'

export type UserDetailsComponentProps = UseUserDetailsReturn & {
  editUserValidator: any
  newUserValidatorClient: any
}

export const UserDetailsComponent = observer(function UserDetailsComponent(props: UserDetailsComponentProps) {
  const {
    user,
    me,
    userId,
    isNewUser,
    isEditingSelf,
    staffV2Enabled,
    workspaceRoles,
    canManageUsers,
    userIsOrg,
    currentWorkspace,
    workspace,
    setWorkspace,
    defaultValues,
    allowedWorkspaces,
    setAllowedWorkspaces,
    allWorkspaces,
    setAllWorkspaces,
    isDeleting,
    position,
    setPosition,
    role,
    setRole,
    roleId,
    setRoleId,
    userType,
    setUserType,
    isDirtyForm,
    setIsDirtyForm,
    selectedStaff,
    setSelectedStaff,
    formRef,
    locationHiddenInputs,
    handleLocationHiddenInputsChange,
    canDelete,
    showRoles,
    deleteUserHandler,
    impersonateUserHandler,
    onAllowedWorkspaceChange,
    removeAllowedWorkspace,
    openStaffModal,
    isNewWorkspaceUser,
    editUserValidator,
    newUserValidatorClient,
    onSubmit,
  } = props

  return (
    <div className="items-start sm:grid xl:flex">
      <div className="flex grow md:grid md:grid-cols-1 md:gap-2">
        <SmartForm<UserType & { _aliases?: string }>
          id={`user-form-${userId}`}
          key={userId}
          className="!mt-0 overflow-auto"
          replace={false}
          validator={isNewUser ? newUserValidatorClient : editUserValidator}
          onSubmit={onSubmit}
          defaultValues={{ ...defaultValues, type: user.type ?? `standard` }}
          hideButtons={true}
          hiddenInputs={[
            { name: `id`, value: user.id },
            { name: `orgId`, value: me.orgId },
            { name: `workspaceId`, value: workspace?.id || currentWorkspace?.id || `` },
            { name: `role`, value: role.toString() },
            ...(staffV2Enabled ? [{ name: `roleId`, value: roleId }] : []),
            ...allowedWorkspaces.map((w) => ({ name: `assignedWorkspaceIds`, value: w?.id })),
            ...locationHiddenInputs,
          ]}
          method="post"
          formRef={formRef}
        >
          <FormPanel title="User details" cols={6}>
            <div className="flex flex-col gap-y-4">
              <FormInput
                cols={6}
                name="name"
                label="Name"
                labelClassName="required text-ui-sm"
                className="rounded-lg border border-default bg-surface px-4 py-3 text-body-sm shadow-elevation-1"
              />
              {hasRole(me, `workspaceAdmin`) && currentWorkspace && !staffV2Enabled && (
                <FormInput
                  cols={6}
                  name="_aliases"
                  label="Aliases, comma separated (Other names this user is referred to as, such as nicknames, etc)"
                  labelClassName="text-ui-sm"
                  className="rounded-lg border border-default bg-surface px-4 py-3 text-body-sm shadow-elevation-1"
                />
              )}
              {!isNewUser && (
                <>
                  <div className="col-span-6">
                    <label className={`text-ui-sm ${position ? `required` : ``}`}>Position</label>
                    <Combo
                      name="position"
                      inputClassName="shadow-elevation-1 text-body-sm text-strong"
                      options={compact(
                        [!position && { name: ``, id: `-1`, value: `` }].concat([
                          { name: `Executive`, id: `Executive`, value: `Executive` },
                          { name: `Consultant`, id: `Consultant`, value: `Consultant` },
                          { name: `Dentist`, id: `Dentist`, value: `Dentist` },
                          { name: `Hygienist`, id: `Hygienist`, value: `Hygienist` },
                          { name: `Manager`, id: `Manager`, value: `Manager` },
                          {
                            name: `Treatment Coordinator`,
                            id: `Treatment Coordinator`,
                            value: `Treatment Coordinator`,
                          },
                          { name: `Administrator`, id: `Administrator`, value: `Administrator` },
                          { name: `Other`, id: `Other`, value: `Other` },
                        ]),
                      )}
                      value={{ id: position ?? `-1`, name: position ?? ``, value: position ?? `` }}
                      onChange={(option) => {
                        setPosition(option.value)
                        setIsDirtyForm(true)
                      }}
                    />
                  </div>
                  {position === `Other` && (
                    <FormInput
                      cols={6}
                      name="jobTitle"
                      label="Title"
                      labelClassName="required text-ui-sm"
                      className="rounded-lg border border-default bg-surface px-4 py-3 text-body-sm shadow-elevation-1"
                      autoComplete="off"
                    />
                  )}
                </>
              )}
              <FormInput
                cols={6}
                name="email"
                label="Email"
                disabled={!isNewUser}
                labelClassName="required text-ui-sm"
                className="rounded-lg border border-default bg-surface px-4 py-3 text-body-sm shadow-elevation-1"
              />
              {!isNewUser && (
                <FormInput
                  cols={6}
                  id="phoneInput"
                  name="phone"
                  label="Phone Number"
                  type="number"
                  className="rounded-lg border border-default bg-surface px-4 py-3 text-body-sm shadow-elevation-1"
                />
              )}
              {!currentWorkspace && me.type === `super` && (
                <div className="col-span-6">
                  <label className="required text-ui-sm">User Type</label>
                  <Combo
                    name="type"
                    className="disabled:bg-surface-subtle"
                    inputClassName="text-body-sm text-strong"
                    options={compact([
                      me.type === `super` && { id: `super`, name: `Super`, value: `super` },
                      { id: `standard`, name: `Standard`, value: `standard` },
                    ])}
                    value={
                      userType
                        ? { id: userType, name: startCase(userType.replace(`_`, ` `)), value: userType }
                        : { id: `standard`, name: `Standard`, value: `standard` }
                    }
                    onChange={(option) => {
                      setUserType((option.value ?? `standard`) as User['type'])
                      setIsDirtyForm(true)
                    }}
                  />
                </div>
              )}
              {role >= legacyRoleRank.orgUser && me.id !== user.id && (
                <div className="col-span-6">
                  <label className="text-ui-sm">Assigned Workspaces</label>
                  <div className="mt-2 flex gap-2 rounded-lg border border-default p-4">
                    <div className="flex w-full items-start gap-2">
                      <label
                        className={classNames(
                          `flex items-center justify-center gap-3 rounded-lg border border-default bg-surface-subtle p-[0.55rem] text-ui-sm text-strong`,
                        )}
                      >
                        All Workspaces{` `}
                        <FormToggle
                          name="allWorkspaces"
                          label="All"
                          defaultValue={!user.assignedWorkspaceIds?.length}
                          disabled={allWorkspaces}
                          onToggle={(isOn) => {
                            if (isOn) {
                              setAllWorkspaces(true)
                              setAllowedWorkspaces([])
                            } else {
                              setAllWorkspaces(false)
                            }
                          }}
                        />
                      </label>
                      <div className="h-full grow py-0">
                        <WorkspaceSelector
                          withBlankOption={false}
                          disabled={!!currentWorkspace || allWorkspaces}
                          workspace={currentWorkspace as WorkspaceJson}
                          selectedWorkspaces={allowedWorkspaces}
                          onChange={(ws) => onAllowedWorkspaceChange(ws)}
                          onRemoveSelectedWorkspace={(ws) => removeAllowedWorkspace(ws)}
                        />
                        {allWorkspaces && (
                          <div className="mt-2 flex gap-2 rounded-lg border border-default p-4">
                            <div className="inline-flex gap-2">
                              <span className="flex items-baseline justify-center gap-3 rounded border border-default bg-surface-subtle p-2 text-body-sm text-strong">
                                All
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </FormPanel>
          {(staffV2Enabled ? canManageUsers : showRoles || hasRole(me, `workspaceAdmin`)) && (
            <FormPanel title="Permissions" cols={6}>
              {(staffV2Enabled ? canManageUsers : showRoles) && (
                <RoleSelector
                  workspaceRoles={workspaceRoles}
                  role={role}
                  roleId={roleId}
                  disabled={isEditingSelf}
                  onChange={(newRole, newRoleId) => {
                    if (newRole !== undefined) setRole(newRole)
                    if (newRoleId !== undefined) setRoleId(newRoleId)
                  }}
                  setIsDirtyForm={setIsDirtyForm}
                />
              )}
              <UserLocationSelector
                user={user}
                role={role}
                isNewUser={isNewUser}
                isNewWorkspaceUser={isNewWorkspaceUser}
                isOrgLevel={role >= legacyRoleRank.orgUser}
                setIsDirtyForm={setIsDirtyForm}
                onHiddenInputsChange={handleLocationHiddenInputsChange}
                staffV2Enabled={staffV2Enabled}
                disabled={isEditingSelf}
              />
            </FormPanel>
          )}
          {staffV2Enabled &&
            canManageUsers &&
            !!currentWorkspace &&
            !workspaceRoles.find((r) => r.id === roleId && !r.workspaceId) && (
              <FormPanel
                title="Staff Assignment"
                cols={6}
                learnMore={<LearnMoreLink to="/settings/staff/learn-more">Learn About Staff</LearnMoreLink>}
              >
                <div className="col-span-6">
                  <FormComboAsyncV2
                    name="staffId"
                    label="Assigned Staff Member"
                    labelClassName="text-ui-sm text-default pb-2"
                    inputClassName="shadow-elevation-1 text-body-sm text-strong"
                    optionsMapper={(res) => res.hits.map((hit) => ({ id: hit.id!, name: hit.name, value: hit.id! }))}
                    fetcher={createGqlFetcher('users')}
                    additionalParams={{ unassignedOnly: `true`, onlyUsers: `true` }}
                    value={selectedStaff}
                    onChange={(option) => {
                      if (selectedStaff && option && selectedStaff.id === option.id) {
                        setSelectedStaff(undefined)
                      } else if (!option || option.id === `-1`) {
                        setSelectedStaff(undefined)
                      } else {
                        setSelectedStaff(option)
                      }
                      setIsDirtyForm(true)
                    }}
                    onAddNewValue={() => openStaffModal()}
                    addText="Add New Staff Member"
                    placeholder="No Linked Staff"
                  />
                </div>
              </FormPanel>
            )}
          {!isNewUser && userIsOrg && !(me.role === legacyRoleRank.orgUser && user.role === me.role) && (
            <FormPanel title="User password details" subtitle="You can manage user's password from here" cols={6}>
              <div className="flex gap-4">
                <input type="submit" value="Resend invite" name="resend" className="btn btn-tertiary btn-md" />
              </div>
            </FormPanel>
          )}
          {!isNewUser && (
            <div className="sticky bottom-0 z-10 col-span-6 flex justify-between bg-transparent py-4">
              <div>
                {canDelete && (
                  <Button
                    mode="danger"
                    label="Delete"
                    size="md"
                    type="button"
                    className="mr-4"
                    onClick={deleteUserHandler}
                    loading={isDeleting}
                  />
                )}
                {me.type === `super` && (
                  <Button
                    mode="danger"
                    label="Impersonate"
                    size="md"
                    type="button"
                    className="mr-4"
                    onClick={impersonateUserHandler}
                  />
                )}
              </div>
              <div></div>
            </div>
          )}
          <div className="mb-6" />
          <div className="z-10">
            <FloatingSave isDirtyForm={isDirtyForm} buttonLabel={isNewUser ? `Save & Send Invite` : `Save Changes`} />
          </div>
        </SmartForm>
      </div>
    </div>
  )
})
